import { IPost } from '_interfaces/post.interface';
import { IMediaItem } from '_interfaces/types/post.types';
import { isAudio, isFile } from 'components/uploaded-images/helpers';
import dayjs from 'dayjs';
import { createContent, isContentEmpty } from 'utils/helpers';
import { firstAddressHelper } from 'utils/helpers/firstAddressHelper';
import { IProjectShort } from 'redux-store/api/projects';
import { IPostUploads, UploadStatusEnum } from 'redux-store/api/activity-feed';

export const getProjectLocation = (project: IProjectShort): string => {
  if (!project) return '';

  const firstAddress = firstAddressHelper(project?.address);
  const address = firstAddress || '';
  const city = project?.city;
  const zip = project?.zip;
  const extraAddress = project.address?.split(',')?.[3] || '';

  return `${address}, ${city}, ${zip} ${extraAddress}`.trim();
};

/** Convert IMediaItem (Firestore format) to IPostUploads (legacy format for shared rendering components) */
const mediaItemToUpload = (item: IMediaItem): IPostUploads => {
  const getMimeType = (): string => {
    switch (item.type) {
      case 'video': return 'video/mp4';
      case 'image': return 'image/jpeg';
      case 'audio': return 'audio/mpeg';
      default: return 'application/octet-stream';
    }
  };

  const getOptions = (): IPostUploads['options'] => {
    if (item.type === 'video') {
      return {
        soundLevel: item.options?.soundLevel || [],
        duration: item.options?.duration || '',
        previewUrl: item.options?.previewUrl || item.transcoded?.thumbnail,
      };
    }
    if (item.type === 'audio') {
      return {
        soundLevel: item.options?.soundLevel || [],
        duration: item.options?.duration || '',
        previewUrl: item.options?.previewUrl,
      };
    }
    return null;
  };

  return {
    id: item.name || item.url,
    url: item.url,
    name: item.name,
    path: item.url,
    mimeType: getMimeType(),
    description: item.description || null,
    location: item.location || null,
    timestamp: item.timestamp || null,
    options: getOptions(),
    status: UploadStatusEnum.COMPLETED,
  };
};

export const getDraftDetails = ({
  draft,
  allProjects,
}: {
  draft: IPost;
  allProjects: IProjectShort[];
}) => {
  const content = createContent(
    draft.message || '',
    draft.mentions ?? [],
    draft.hashtags ?? []
  );
  const uploads = (draft.media || []).map(mediaItemToUpload);
  const media = uploads.filter(({ mimeType, name }) => !isFile(name) && !isAudio(mimeType));
  const project = allProjects?.find((p) => p.id === Number(draft.projectId));
  const formattedCreatedAt = dayjs(draft.createdAt).format('MMM D / h:mm A');

  const hasMedia = media.length > 0;
  const hasContent = content && !isContentEmpty(content);
  const hasMediaOrContent = hasMedia || hasContent;

  return {
    content,
    media,
    hasMedia,
    hasContent,
    hasMediaOrContent,
    postfix: 'Post',
    project: {
      id: project?.id,
      name: project?.name,
      location: getProjectLocation(project),
      createdAt: formattedCreatedAt,
    },
    uploads,
    completePrefix: 'Complete',
  };
};
